import { type PropsWithChildren, type ReactNode, useCallback, useMemo, useState } from 'react'
import { useLocales } from '@sevenrooms/locales'
import { useTheme } from '@sevenrooms/ui-kit'
import { Box, Grid, VStack, Flex } from '@sevenrooms/ui-kit/layout'
import { getTimeslotDescription } from '../../../../../components/PrimaryAvailability/ReservationAvailability'
import { ReservationTimeslot, type ReservationTimeSlotVariant } from '../../../../../components/ReservationTimeslot'
import { LinkButton } from '../../LinkButton/LinkButton'
import { ExpandCollapseButton } from './ExpandCollapseButton'
import type { AvailabilityTimeWithUpSellCost } from '../../../../../hooks'

interface ColumnTimeSlotsProps {
  timeslots?: AvailabilityTimeWithUpSellCost[]
  timeSlotVariant?: ReservationTimeSlotVariant
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  onDateLabelClick?: () => void
  startDate?: string
  requestOrPriorityAlertAvailability?: ReactNode
  timeSlotsDisplayCount?: number
  descriptionType?: 'short' | 'full'
  currencyCode: string
}

export function ColumnTimeSlots({
  timeslots,
  timeSlotVariant = 'primary',
  timeSlotsDisplayCount = 2,
  startDate,
  requestOrPriorityAlertAvailability,
  descriptionType = 'full',
  currencyCode,
  onTimeSlotClick,
  onDateLabelClick,
}: PropsWithChildren<ColumnTimeSlotsProps>) {
  const { formatMessage } = useLocales()
  const theme = useTheme()
  const timeSlotsCount = useMemo(() => {
    let itemsCount = timeslots?.length || 0
    if (requestOrPriorityAlertAvailability) {
      itemsCount += 1
    }
    return itemsCount
  }, [requestOrPriorityAlertAvailability, timeslots?.length])

  const columnsCount = timeSlotsCount < timeSlotsDisplayCount ? timeSlotsCount : timeSlotsDisplayCount
  const showExpandCollapseButton = timeSlotsCount > columnsCount
  const gridTemplate = `repeat(${columnsCount}, minmax(0, 1fr))`
  const [collapsed, setCollapsed] = useState(true)
  const onCollapsedClick = useCallback((collapsed: boolean) => {
    setCollapsed(collapsed)
  }, [])
  return (
    <VStack spacing="sm">
      {startDate && (
        <Flex>
          <LinkButton text={startDate} onClick={onDateLabelClick} data-test="sr-room-card-date-label-button" />
        </Flex>
      )}
      <Grid
        gridAutoColumns="auto auto"
        gridTemplateColumns={gridTemplate}
        gap="s"
        alignItems="start"
        data-test="sr-room-card-availability-container"
        maxHeight={collapsed ? theme.fieldHeight.l : 'initial'}
        overflow="hidden"
      >
        {timeslots?.map(timeSlotInfo => (
          <ReservationTimeslot
            variant={timeSlotVariant}
            size="l"
            key={`${timeSlotInfo.time}${timeSlotInfo.accessPersistentId ?? ''}`}
            timeslotDescription={
              descriptionType === 'full'
                ? getTimeslotDescription(
                    formatMessage,
                    timeSlotInfo.publicTimeSlotDescription,
                    timeSlotInfo.showCost,
                    (timeSlotInfo.cost ?? 0) + timeSlotInfo.includedUpgradesCost,
                    timeSlotInfo.chargeType,
                    timeSlotInfo.duration,
                    currencyCode,
                    timeSlotInfo.fees
                  )
                : timeSlotInfo.publicTimeSlotDescription
            }
            time={timeSlotInfo.time}
            onClick={() => {
              onTimeSlotClick?.(timeSlotInfo)
            }}
          />
        ))}
        {requestOrPriorityAlertAvailability}
      </Grid>
      {showExpandCollapseButton && (
        <Box>
          <ExpandCollapseButton collapsed={collapsed} onClick={onCollapsedClick} />
        </Box>
      )}
    </VStack>
  )
}
